import { useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Check, CreditCard, Phone, ArrowRight, Shield, Clock, LogOut, Copy, ExternalLink, Building2, Upload, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const LOGO_URL = "https://customer-assets.emergentagent.com/job_praxis-launch/artifacts/9x9a538r_MeritumPraxis%20Logo.png";

const plans = [
  {
    id: 'monthly',
    name: 'Monthly',
    price: 'R35',
    period: '/month',
    description: 'Flexible monthly access',
    features: ['Full professional profile', 'PDF profile download', 'Portfolio showcase', 'Skill-based discovery', 'Cancel anytime'],
  },
  {
    id: 'annual',
    name: 'Annual',
    price: 'R360',
    period: '/year',
    description: 'Save R60 per year!',
    badge: 'Best Value',
    features: ['Everything in Monthly', 'Save R60 annually', 'Priority profile visibility', 'Early access to new features', 'Annual commitment discount'],
  },
];

export default function PlanSelection() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planFromUrl = searchParams.get('plan');
  const [selectedPlan, setSelectedPlan] = useState(planFromUrl === 'monthly' ? 'monthly' : 'annual');
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [showCallbackForm, setShowCallbackForm] = useState(false);
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackSubmitted, setCallbackSubmitted] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [copiedField, setCopiedField] = useState('');
  const [proofImage, setProofImage] = useState(null);
  const [submittingProof, setSubmittingProof] = useState(false);
  const [submissionResult, setSubmissionResult] = useState('');

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedField(field);
      setTimeout(() => setCopiedField(''), 2000);
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    navigate('/login');
  };

  const handleStartTrial = () => {
    setStartingTrial(true);
    setTimeout(() => {
      navigate('/dashboard');
    }, 1500);
  };

  const handleRequestCallback = () => {
    setCallbackSubmitted(true);
    setTimeout(() => {
      setShowCallbackForm(false);
    }, 2000);
  };

  const handlePaymentSelect = (method) => {
    setPaymentMethod(method);
    setSubmissionResult('');
    setProofImage(null);
    if (method === 'callback') {
      setShowCallbackForm(true);
    } else {
      setShowCallbackForm(false);
    }
  };

  const handleProofUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { alert('Image must be under 5MB'); return; }
    const reader = new FileReader();
    reader.onloadend = () => setProofImage(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSubmitPayment = async (method) => {
    setSubmittingProof(true);
    setSubmissionResult('');
    try {
      const amount = selectedPlan === 'annual' ? 360 : 35;
      await axios.post(`${API}/payments/submit`, {
        method, plan_type: selectedPlan, amount,
        reference: user.email,  // user's email is the actual payment identifier; admin still sees the user
        proof_image: proofImage,
      }, { headers: getAuthHeaders() });
      setSubmissionResult('Submission received! Our team will verify and activate your account within 24 hours.');
      setProofImage(null);
    } catch (e) {
      setSubmissionResult('Submission failed. Please try again or contact support.');
    } finally {
      setSubmittingProof(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-meritum-navy via-meritum-navy to-[#062240]" data-testid="plan-selection-page">
      {/* Header */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <div className="text-center mb-10">
          <div className="flex items-center justify-between mb-6">
            <div />
            <Link to="/">
              <img src={LOGO_URL} alt="MeritumPraxis" className="h-16" />
            </Link>
            <Button onClick={handleLogout} variant="outline" size="sm" className="border-white/30 text-white hover:bg-white/10 font-body" data-testid="plan-logout-btn">
              <LogOut size={14} className="mr-1" />Logout
            </Button>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-3">
            Welcome, {user.name || 'there'}!
          </h1>
          <p className="font-body text-slate-300 text-base max-w-lg mx-auto">
            Choose a plan to unlock your professional profile. Start with a <span className="text-meritum-gold font-semibold">14-day free trial</span> — no commitment required.
          </p>
        </div>

        {/* Free Trial Banner */}
        <div className="bg-meritum-gold/10 border border-meritum-gold/30 rounded-xl p-5 mb-8 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-meritum-gold/20 rounded-full flex items-center justify-center">
              <Clock size={20} className="text-meritum-gold" />
            </div>
            <div>
              <p className="font-heading text-white font-semibold">14-Day Free Trial</p>
              <p className="font-body text-sm text-slate-300">Try all features free. No payment required upfront.</p>
            </div>
          </div>
          <Button
            onClick={handleStartTrial}
            disabled={startingTrial}
            className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold px-8"
            data-testid="start-trial-btn"
          >
            {startingTrial ? 'Starting...' : 'Start Free Trial'}
            <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>

        {/* Plan Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-10">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              data-testid={`plan-card-${plan.id}`}
              className={`relative rounded-xl p-6 cursor-pointer transition-all duration-200 border-2 ${
                selectedPlan === plan.id
                  ? 'border-meritum-gold bg-white/10 shadow-lg shadow-meritum-gold/10'
                  : 'border-white/10 bg-white/5 hover:border-white/30'
              }`}
            >
              {plan.badge && (
                <span className="absolute -top-3 right-4 bg-meritum-gold text-meritum-navy text-xs font-bold px-3 py-1 rounded-full">{plan.badge}</span>
              )}
              <div className="flex items-baseline gap-1 mb-1">
                <span className="font-heading text-3xl font-bold text-white">{plan.price}</span>
                <span className="font-body text-slate-400">{plan.period}</span>
              </div>
              <h3 className="font-heading text-lg font-semibold text-white">{plan.name}</h3>
              <p className="font-body text-sm text-meritum-gold mb-4">{plan.description}</p>
              <ul className="space-y-2">
                {plan.features.map((f, i) => (
                  <li key={i} className="flex items-center gap-2 font-body text-sm text-slate-300">
                    <Check size={14} className="text-meritum-gold flex-shrink-0" />{f}
                  </li>
                ))}
              </ul>
              {selectedPlan === plan.id && (
                <div className="absolute top-4 left-4 w-5 h-5 bg-meritum-gold rounded-full flex items-center justify-center">
                  <Check size={12} className="text-meritum-navy" />
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Payment Methods */}
        <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8">
          <h3 className="font-heading text-lg font-semibold text-white mb-4">Payment Method</h3>
          <div className="grid sm:grid-cols-3 gap-4">
            <button
              onClick={() => handlePaymentSelect('eft')}
              data-testid="payment-eft"
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                paymentMethod === 'eft'
                  ? 'border-meritum-gold bg-meritum-gold/10'
                  : 'border-white/10 hover:border-white/30 bg-white/5'
              }`}
            >
              <Building2 size={24} className={paymentMethod === 'eft' ? 'text-meritum-gold' : 'text-slate-400'} />
              <p className="font-heading text-white font-semibold mt-2">Bank Transfer (EFT)</p>
              <p className="font-body text-xs text-slate-400 mt-1">Direct deposit to our FNB account</p>
            </button>

            <button
              onClick={() => handlePaymentSelect('paypal')}
              data-testid="payment-paypal"
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                paymentMethod === 'paypal'
                  ? 'border-meritum-gold bg-meritum-gold/10'
                  : 'border-white/10 hover:border-white/30 bg-white/5'
              }`}
            >
              <svg viewBox="0 0 24 24" width="24" height="24" className={paymentMethod === 'paypal' ? 'text-meritum-gold' : 'text-slate-400'} fill="currentColor">
                <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z"/>
              </svg>
              <p className="font-heading text-white font-semibold mt-2">PayPal</p>
              <p className="font-body text-xs text-slate-400 mt-1">Pay securely via PayPal</p>
            </button>

            <button
              onClick={() => handlePaymentSelect('callback')}
              data-testid="payment-callback"
              className={`p-4 rounded-lg border-2 transition-all text-left ${
                paymentMethod === 'callback'
                  ? 'border-meritum-gold bg-meritum-gold/10'
                  : 'border-white/10 hover:border-white/30 bg-white/5'
              }`}
            >
              <Phone size={24} className={paymentMethod === 'callback' ? 'text-meritum-gold' : 'text-slate-400'} />
              <p className="font-heading text-white font-semibold mt-2">Request a Call</p>
              <p className="font-body text-xs text-slate-400 mt-1">We'll help you with payment</p>
            </button>
          </div>

          {/* EFT Banking Details */}
          {paymentMethod === 'eft' && (
            <div className="mt-6 p-5 bg-white/5 rounded-lg border border-white/10" data-testid="eft-details">
              <h4 className="font-heading text-white font-semibold mb-2">Bank Transfer Details</h4>
              <p className="font-body text-sm text-slate-300 mb-4">Transfer <span className="text-meritum-gold font-bold">{selectedPlan === 'annual' ? 'R360' : 'R35'}</span> to the following account. Use <span className="text-meritum-gold font-semibold">info@meritumpraxis.co.za</span> as the payment reference.</p>

              <div className="space-y-3 bg-white/5 rounded-lg p-4 border border-white/10">
                {[
                  { label: 'Account Name', value: 'MERITUMPRAXIS (PTY) LTD', key: 'name' },
                  { label: 'Bank', value: 'First National Bank Limited (FNB)', key: 'bank' },
                  { label: 'Account Number', value: '63200790970', key: 'account' },
                  { label: 'Branch Code', value: '256505', key: 'branch' },
                  { label: 'Swift Code', value: 'FIRNZAJJ', key: 'swift' },
                  { label: 'Reference', value: 'info@meritumpraxis.co.za', key: 'ref' },
                ].map(({ label, value, key }) => (
                  <div key={key} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                    <div>
                      <p className="font-body text-xs text-slate-400">{label}</p>
                      <p className="font-body text-sm text-white font-medium" data-testid={`eft-${key}`}>{value}</p>
                    </div>
                    <button
                      onClick={() => copyToClipboard(value, key)}
                      className="p-1.5 rounded hover:bg-white/10 transition-colors"
                      title={`Copy ${label}`}
                      data-testid={`copy-${key}-btn`}
                    >
                      {copiedField === key ? (
                        <Check size={14} className="text-emerald-400" />
                      ) : (
                        <Copy size={14} className="text-slate-400" />
                      )}
                    </button>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 mt-4 text-xs text-slate-400">
                <Shield size={14} className="text-meritum-gold flex-shrink-0" />
                <span className="font-body">Once your payment is received, your account will be activated within 24 hours.</span>
              </div>

              {/* Proof of payment upload */}
              <div className="mt-5 pt-4 border-t border-white/10">
                <p className="font-heading text-sm text-white font-semibold mb-2">After paying, submit proof here:</p>
                <label className="block cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleProofUpload} className="hidden" data-testid="eft-proof-input" />
                  <div className={`flex items-center gap-3 p-3 rounded-lg border-2 border-dashed transition ${proofImage ? 'border-emerald-400/40 bg-emerald-400/5' : 'border-white/20 hover:border-meritum-gold/50 bg-white/5'}`}>
                    {proofImage ? (
                      <><Check size={18} className="text-emerald-400 flex-shrink-0" /><span className="font-body text-xs text-emerald-300 truncate">Proof attached - ready to submit</span></>
                    ) : (
                      <><Upload size={18} className="text-meritum-gold flex-shrink-0" /><span className="font-body text-xs text-slate-300">Upload proof of payment (screenshot/receipt, max 5MB)</span></>
                    )}
                  </div>
                </label>
                <Button onClick={() => handleSubmitPayment('eft')} disabled={submittingProof || !proofImage} className="mt-3 w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold disabled:opacity-50" data-testid="submit-eft-payment-btn">
                  {submittingProof ? <><Loader2 size={16} className="mr-2 animate-spin" />Submitting...</> : <>Submit for verification</>}
                </Button>
                {submissionResult && <p className="font-body text-xs text-emerald-300 mt-2 text-center" data-testid="eft-result">{submissionResult}</p>}
              </div>
            </div>
          )}

          {/* PayPal */}
          {paymentMethod === 'paypal' && (
            <div className="mt-6 p-5 bg-white/5 rounded-lg border border-white/10 text-center" data-testid="paypal-section">
              <p className="font-body text-slate-300 mb-4">You will be redirected to PayPal to complete your <span className="text-meritum-gold font-semibold">{selectedPlan === 'annual' ? 'R360/year' : 'R35/month'}</span> payment.</p>
              <a href="https://www.paypal.com/ncp/payment/C47W6PYJGGVHE" target="_blank" rel="noopener noreferrer">
                <Button className="bg-[#0070ba] text-white hover:bg-[#005ea6] font-body font-bold px-8" data-testid="paypal-pay-btn">
                  <ExternalLink size={16} className="mr-2" />Pay with PayPal - {selectedPlan === 'annual' ? 'R360/year' : 'R35/month'}
                </Button>
              </a>
              <p className="font-body text-xs text-slate-400 mt-3">You will be taken to PayPal's secure payment page.</p>

              {/* Confirm PayPal payment */}
              <div className="mt-5 pt-4 border-t border-white/10 text-left">
                <p className="font-heading text-sm text-white font-semibold mb-2">After paying on PayPal, confirm here:</p>
                <label className="block cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleProofUpload} className="hidden" data-testid="paypal-proof-input" />
                  <div className={`flex items-center gap-3 p-3 rounded-lg border-2 border-dashed transition ${proofImage ? 'border-emerald-400/40 bg-emerald-400/5' : 'border-white/20 hover:border-meritum-gold/50 bg-white/5'}`}>
                    {proofImage ? (
                      <><Check size={18} className="text-emerald-400 flex-shrink-0" /><span className="font-body text-xs text-emerald-300 truncate">PayPal receipt attached</span></>
                    ) : (
                      <><Upload size={18} className="text-meritum-gold flex-shrink-0" /><span className="font-body text-xs text-slate-300">Optional: upload PayPal receipt screenshot</span></>
                    )}
                  </div>
                </label>
                <Button onClick={() => handleSubmitPayment('paypal')} disabled={submittingProof} className="mt-3 w-full bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold disabled:opacity-50" data-testid="submit-paypal-payment-btn">
                  {submittingProof ? <><Loader2 size={16} className="mr-2 animate-spin" />Submitting...</> : <>I've completed PayPal payment</>}
                </Button>
                {submissionResult && <p className="font-body text-xs text-emerald-300 mt-2 text-center" data-testid="paypal-result">{submissionResult}</p>}
              </div>
            </div>
          )}

          {/* Callback Form */}
          {showCallbackForm && (
            <div className="mt-6 p-5 bg-white/5 rounded-lg border border-white/10" data-testid="callback-form">
              {callbackSubmitted ? (
                <div className="text-center py-4">
                  <Check size={40} className="text-green-400 mx-auto mb-2" />
                  <p className="font-heading text-white font-semibold">Request Submitted!</p>
                  <p className="font-body text-sm text-slate-300 mt-1">Our team will call you shortly to help with your payment.</p>
                </div>
              ) : (
                <>
                  <h4 className="font-heading text-white font-semibold mb-4">Request a Callback</h4>
                  <p className="font-body text-sm text-slate-300 mb-4">Enter your phone number and our team will call you to assist with payment.</p>
                  <div className="flex gap-3 max-w-md">
                    <Input
                      value={callbackPhone}
                      onChange={(e) => setCallbackPhone(e.target.value)}
                      placeholder="+27 XX XXX XXXX"
                      className="bg-white/10 border-white/20 text-white placeholder:text-slate-500"
                      data-testid="callback-phone-input"
                    />
                    <Button onClick={handleRequestCallback} className="bg-meritum-gold text-meritum-navy hover:bg-meritum-gold/90 font-body font-bold whitespace-nowrap" data-testid="submit-callback-btn">
                      Request Call
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Skip / Continue */}
        <div className="text-center pb-12">
          <button
            onClick={() => navigate('/dashboard')}
            className="font-body text-sm text-slate-400 hover:text-white underline transition-colors"
            data-testid="skip-plan-btn"
          >
            Skip for now and go to dashboard
          </button>
        </div>
      </div>
    </div>
  );
}
